import React, { useEffect, useState } from "react";
import { AlertTriangle, History, Loader2, ShieldCheck, Trash2, X } from "lucide-react";
import { apiFetch } from "../utils/apiClient";

const makeOperationUuid = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `sale-void-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const money = (value) =>
  `${new Intl.NumberFormat("fr-FR").format(Math.round(Number(value) || 0))} F`;

export const resolveSaleOrderId = (sale = {}) =>
  Number(sale.order_id || sale.orderId || sale.id);

export const SaleVoidDialog = ({ sale, isOpen, onClose, onSuccess }) => {
  const [reason, setReason] = useState("");
  const [adminPin, setAdminPin] = useState("");
  const [operationUuid, setOperationUuid] = useState(makeOperationUuid);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !sale) return;
    setReason("");
    setAdminPin("");
    setError("");
    setSubmitting(false);
    setOperationUuid(makeOperationUuid());
  }, [isOpen, sale]);

  if (!isOpen || !sale) return null;
  // Une ligne de journal peut aussi porter un identifiant de paiement. La
  // commande explicite reste donc toujours prioritaire lorsqu'elle existe.
  const orderId = resolveSaleOrderId(sale);
  const label = sale.commandeId || sale.payment_number || sale.order_number || `#${orderId}`;
  const amount = sale.amount ?? sale.total_paid_amount ?? sale.total_amount;

  const submit = async (event) => {
    event.preventDefault();
    if (!reason.trim() || !adminPin.trim() || !orderId || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await apiFetch(`/api/sales/${orderId}/void`, {
        includeBearer: true,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: reason.trim(),
          admin_pin: adminPin.trim(),
          operation_uuid: operationUuid,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Annulation impossible.");
      await onSuccess?.(payload);
      onClose?.();
    } catch (submitError) {
      setError(submitError?.message || "Annulation impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
      <form onSubmit={submit} className="w-full max-w-xl overflow-hidden rounded-3xl border border-red-400/25 bg-[#071126] text-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-white/10 p-6">
          <div className="flex gap-4">
            <div className="rounded-2xl bg-red-500/15 p-3 text-red-300"><AlertTriangle size={26} /></div>
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-red-300">Annulation administrative</p>
              <h2 className="mt-1 text-2xl font-bold">Annuler la vente {label}</h2>
              <p className="mt-1 text-white/60">{money(amount)}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting} className="rounded-xl border border-white/10 p-2 text-white/70 hover:bg-white/10"><X /></button>
        </div>
        <div className="space-y-5 p-6">
          <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 p-4 text-sm text-amber-100">
            Cette vente sera retirée du Journal, des ventes et des rapports. Les consommations WAISE encore restituables retourneront au stock. Aucun paiement à crédit ne peut être annulé ici.
          </div>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold">Motif obligatoire</span>
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} maxLength={500} placeholder="Ex. vente enregistrée par erreur" className="w-full rounded-2xl border border-white/15 bg-black/20 px-4 py-3 outline-none focus:border-amber-400" />
          </label>
          <label className="block">
            <span className="mb-2 flex items-center gap-2 text-sm font-semibold"><ShieldCheck size={17} /> Code PIN d’un admin actif</span>
            <input type="password" inputMode="numeric" autoComplete="off" value={adminPin} onChange={(event) => setAdminPin(event.target.value)} placeholder="Code confidentiel" className="w-full rounded-2xl border border-white/15 bg-black/20 px-4 py-3 outline-none focus:border-amber-400" />
          </label>
          {error && <div className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>}
        </div>
        <div className="flex gap-3 border-t border-white/10 p-6">
          <button type="button" onClick={onClose} disabled={submitting} className="flex-1 rounded-2xl border border-white/15 px-5 py-3 font-semibold hover:bg-white/5">Retour</button>
          <button type="submit" disabled={submitting || !reason.trim() || !adminPin.trim()} className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-red-500 px-5 py-3 font-bold text-white disabled:cursor-not-allowed disabled:opacity-45">
            {submitting ? <Loader2 className="animate-spin" size={19} /> : <ShieldCheck size={19} />}
            Confirmer l’annulation
          </button>
        </div>
      </form>
    </div>
  );
};

export const SaleVoidHistoryDialog = ({ isOpen, onClose, currentUser }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const isSuperadmin =
    String(currentUser?.role || "").trim().toLowerCase() === "superadmin";
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError("");
    apiFetch("/api/sales/void-history", {
      includeBearer: true,
      cache: "no-store",
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ([]));
        if (!response.ok) throw new Error(payload.error || "Historique indisponible.");
        setRows(Array.isArray(payload) ? payload : []);
      })
      .catch((loadError) => setError(loadError.message))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const deleteHistoryRow = async (row) => {
    if (!isSuperadmin || deletingId) return;
    const confirmed = window.confirm(
      "Retirer cette ligne de l’historique visible ? La vente restera annulée et la trace technique sera conservée.",
    );
    if (!confirmed) return;
    setDeletingId(row.id);
    setError("");
    try {
      const response = await apiFetch(`/api/sales/void-history/${row.id}`, {
        includeBearer: true,
        method: "DELETE",
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Suppression impossible.");
      }
      setRows((current) => current.filter((entry) => entry.id !== row.id));
    } catch (deleteError) {
      setError(deleteError?.message || "Suppression impossible.");
    } finally {
      setDeletingId(null);
    }
  };
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#071126] text-white">
        <div className="flex items-center justify-between border-b border-white/10 p-6"><div className="flex items-center gap-3"><History className="text-amber-300" /><h2 className="text-2xl font-bold">Historique des annulations</h2></div><button onClick={onClose} className="rounded-xl p-2 hover:bg-white/10"><X /></button></div>
        <div className="overflow-y-auto p-6">
          {loading ? <p className="text-white/60">Chargement…</p> : error ? <p className="text-red-300">{error}</p> : rows.length === 0 ? <p className="text-white/60">Aucune vente annulée.</p> : (
            <div className="space-y-3">{rows.map((row) => <div key={row.id} className="rounded-2xl border border-white/10 bg-white/5 p-4"><div className="flex flex-wrap justify-between gap-3"><div><p className="font-bold">Vente #{row.representative_order_id}</p><p className="text-sm text-white/60">{row.reason}</p></div><div className="flex items-start gap-3"><div className="text-right"><p className="font-mono font-bold text-red-200">-{money(row.amount)}</p><p className="text-xs text-white/50">{new Date(row.created_at).toLocaleString("fr-FR")}</p></div>{isSuperadmin && <button type="button" onClick={() => deleteHistoryRow(row)} disabled={deletingId === row.id} className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:cursor-wait disabled:opacity-50" title="Retirer de l’historique visible" aria-label={`Supprimer la ligne d’annulation ${row.id}`}>{deletingId === row.id ? <Loader2 className="animate-spin" size={17} /> : <Trash2 size={17} />}</button>}</div></div><p className="mt-2 text-xs text-white/50">Demandée par {row.requested_by_name || "—"} · validée par {row.authorized_by_name || "—"}</p></div>)}</div>
          )}
        </div>
      </div>
    </div>
  );
};
